"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Button,
  Card,
  CardBody,
  Tabs,
  Tab,
  Spinner,
  Avatar
} from "@heroui/react";
import { Typography } from "../../../../components";
import { staffApi, Partner } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { usePageBreadcrumbs } from "../../../../hooks/usePageBreadcrumbs";
import { BalanceHistoryTab, PartnerCPLOffersTab, PartnerRevShareCampaignsTab, WithdrawalRequestsTab } from "./components";
import { refShareApi } from "../../../../api/services";
import { getPartnerStatusConfig, getPartnerLevelConfig } from "../constants";

interface PartnerDetailsPageProps {}

/**
 * PartnerDetailsPage Component
 *
 * Страница деталей партнера в административной панели.
 */
const PartnerDetailsPage: React.FC<PartnerDetailsPageProps> = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslations();

  // State
  const [partnerData, setPartnerData] = useState<Partner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<string>("0");
  const [withdrawalRequestsCount, setWithdrawalRequestsCount] = useState<number>(0);
  const [cplOffersCount, setCplOffersCount] = useState<number>(0);
  const [revShareCampaignsCount, setRevShareCampaignsCount] = useState<number>(0);

  // Определяем активный таб из URL или устанавливаем по умолчанию
  const activeTab = tab || "balance";

  // Валидные табы
  const validTabs = ["balance", "cpl-offers", "revshare", "withdrawal-requests"];

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.partners'), href: '/users/partners' },
    { label: partnerData?.name || '...', isActive: true }
  ], [t, partnerData?.name]);

  usePageBreadcrumbs(breadcrumbs);

  // Fetch partner data
  const fetchPartnerData = async () => {
    if (!id) {
      setError("Partner ID is required");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await staffApi.getPartner(id);
      setPartnerData(data);
    } catch (error: any) {
      console.error("Error fetching partner data:", error);
      setError(error?.response?.data?.message || "Failed to load partner data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerData();
    fetchBalance();
    fetchWithdrawalRequestsCount();
    fetchCPLOffersCount();
    fetchRevShareCampaignsCount();
  }, [id]);

  // Fetch balance
  const fetchBalance = async () => {
    if (!id) return;

    try {
      const balanceResponse = await staffApi.getPartnerBalance(id);
      setBalance(balanceResponse.balance);
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  // Fetch withdrawal requests count
  const fetchWithdrawalRequestsCount = async () => {
    if (!id) return;

    try {
      const response = await staffApi.getWithdrawalRequests({
        page: 1,
        page_size: 1,
        owner: id,
      });
      setWithdrawalRequestsCount(response.count);
    } catch (error) {
      console.error("Error fetching withdrawal requests count:", error);
    }
  };

  // Fetch CPL offers count
  const fetchCPLOffersCount = async () => {
    if (!id) return;

    try {
      const response = await staffApi.getPartnerCPLOffers(id, {
        page: 1,
        page_size: 1,
      });
      setCplOffersCount(response.count);
    } catch (error) {
      console.error("Error fetching CPL offers count:", error);
    }
  };

  // Fetch RevShare campaigns count
  const fetchRevShareCampaignsCount = async () => {
    if (!id) return;

    try {
      const response = await refShareApi.getOrders({
        page: 1,
        page_size: 1,
        owner: id,
      });
      setRevShareCampaignsCount(response.count);
    } catch (error) {
      console.error("Error fetching RevShare campaigns count:", error);
    }
  };

  // Проверяем валидность таба и перенаправляем на дефолтный если нужно
  useEffect(() => {
    if (tab && !validTabs.includes(tab)) {
      navigate(`/users/partners/${id}/balance`, { replace: true });
    } else if (!tab) {
      navigate(`/users/partners/${id}/balance`, { replace: true });
    }
  }, [tab, id, navigate]);

  const handleTabChange = (key: string) => {
    navigate(`/users/partners/${id}/${key}`);
  };

  const handleBackToList = () => {
    navigate('/users/partners');
  };

  // Статистические карточки
  const StatCard = ({ title, value, className = "" }: { title: string; value: string | number; className?: string }) => (
    <Card className={`w-full border-0 rounded-none shadow-none ${className}`}>
      <CardBody className="px-[16px] py-[12px]">
        <Typography variant="body2" className="text-[#52525b] mb-[4px] text-[14px] leading-[18px] font-medium">
          {title}
        </Typography>
        <Typography variant="body3" className="text-[#3f3f46] text-[14px] break-words">
          {value}
        </Typography>
      </CardBody>
    </Card>
  );

  // Форматирование даты
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получить конфиг статуса из constants
  const getStatusConfig = (status: Partner["status"]) => {
    return getPartnerStatusConfig(status, t);
  };

  // Получить конфиг уровня из constants
  const getLevelConfig = (lvl: Partner["lvl"]) => {
    return getPartnerLevelConfig(lvl, t);
  };

  // Компонент для заголовка таба с бейджем
  const TabTitle = ({ label, badge, isGreen = false }: { label: string; badge?: string | number; isGreen?: boolean }) => (
    <div className="flex items-center gap-2">
      <span>{label}</span>
      {badge !== undefined && (
        <span
          className={`inline-flex h-[20px] min-w-[28px] items-center justify-center rounded-full px-[8px] text-[12px] font-medium ${
            isGreen
              ? 'bg-[#17C964]/10 text-[#17C964]'
              : 'bg-[#D4D4D8]/40 text-[#71717A]'
          }`}
        >
          {badge}
        </span>
      )}
    </div>
  );

  // Контент табов
  const renderTabContent = () => {
    if (!partnerData) return null;

    switch (activeTab) {
      case "balance":
        return <BalanceHistoryTab partnerId={id!} />;

      case "cpl-offers":
        return <PartnerCPLOffersTab partnerId={id!} />;

      case "revshare":
        return <PartnerRevShareCampaignsTab partnerId={id!} />;

      case "withdrawal-requests":
        return <WithdrawalRequestsTab partnerId={id!} />;

      default:
        return <BalanceHistoryTab partnerId={id!} />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  // Error state
  if (error || !partnerData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Typography variant="h5" className="text-danger">
          {error || "Partner not found"}
        </Typography>
        <Button color="primary" onPress={handleBackToList}>
          {t('admin.partners.backToList')}
        </Button>
      </div>
    );
  }
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  return (
    <div className="flex flex-col">
      <div className="px-6 flex flex-col">
        {/* Back Button and Title */}
        <div className="mb-[32px] flex items-center gap-4">
          <Typography variant="h5" className="text-[#11181C] font-semibold">
            {partnerData.name}
          </Typography>
        </div>

        {/* Main Content */}
        <div className="flex flex-col xl:flex-row gap-6">
          {/* Left Sidebar */}
          <div className="w-full flex flex-row items-start gap-[14px] xl:w-56 xl:flex-col flex-shrink-0">
            {/* Avatar and Info */}
            <div className="mb-6 flex flex-col gap-4 xl:flex-col xl:gap-0">
              <div className="w-[200px] h-[200px] flex-shrink-0 rounded-xl overflow-hidden flex flex-row xl:mb-[16px] bg-gray-100 flex items-center justify-center">
                <Avatar
                  src={partnerData.avatar}
                  alt={partnerData.name}
                  showFallback
                  name={getInitials(partnerData.name)}
                  size="sm"
                  className="w-full h-full rounded-[12px] bg-gray-200"
                  classNames={{
                    fallback: "bg-gray-200 text-gray-600"
                  }}
                />
              </div>
              <div className="flex-1 max-w-[200px] xl:mb-4 flex flex-row !flex-wrap items-start xl:flex-col gap-2">

                {/* Status Chip */}
                <span
                  className="inline-flex items-center justify-center rounded-lg px-[8px] py-[2px] text-[11px] font-medium whitespace-nowrap"
                  style={{
                    backgroundColor: `${getStatusConfig(partnerData.status).color}20`,
                    color: getStatusConfig(partnerData.status).color
                  }}
                >
                  {getStatusConfig(partnerData.status).label}
                </span>

                {/* Level Chip */}
                <span
                  className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium capitalize whitespace-nowrap"
                  style={{
                    backgroundColor: getLevelConfig(partnerData.lvl).bg,
                    color: getLevelConfig(partnerData.lvl).text
                  }}
                >
                  {getLevelConfig(partnerData.lvl).label}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-[14px]">
              {/* Contact Cards */}
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden xl:mb-4">
                <StatCard
                  title={t('admin.partners.details.email')}
                  value={partnerData.email || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.partners.details.telegram')}
                  value={partnerData.tg_tag || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.partners.details.whatsApp')}
                  value={partnerData.whats_app || '—'}
                />
              </div>

              {/* Additional Info Cards */}
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden xl:mb-4">
                <StatCard
                  title={t('admin.partners.details.language')}
                  value={partnerData.language || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.partners.details.volume')}
                  //@ts-ignore
                  value={partnerData?.traffic_volume || partnerData.volume || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.partners.details.sourceInfoAboutUs')}
                  value={partnerData.source_info_about_us || '—'}
                />
              </div>

              {/* OnlyFans Experience */}
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden xl:mb-4">
                <StatCard
                  title={t('admin.partners.details.hasOnlyFansExperience')}
                  value={partnerData.has_only_fans_experience ? t('global.common.yes') : t('global.common.no')}
                />
                {partnerData.has_only_fans_experience && partnerData.has_only_fans_experience_description && (
                  <>
                    <div className="border-t !border-[#E5E5E5]"></div>
                    <StatCard
                      title={t('admin.partners.details.experienceDescription')}
                      value={partnerData.has_only_fans_experience_description}
                    />
                  </>
                )}
              </div>

              {/* Created Date */}
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden">
                <StatCard
                  title={t('admin.partners.details.createdAt')}
                  value={formatDate(partnerData.created_at)}
                />
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col">
            {/* Tabs */}
            <div className="mb-[32px]">
              <div className="bg-[#f4f4f5] rounded-xl w-fit">
                <Tabs
                  selectedKey={activeTab}
                  onSelectionChange={(key: any) => handleTabChange(key as string)}
                  variant="light"
                  classNames={{
                    tabList: "gap-1",
                    cursor: "bg-white rounded-lg shadow-sm",
                    tab: "px-[12px] py-[4px] text-sm font-normal data-[selected=true]:text-black text-[#71717a]",
                    tabContent: "group-data-[selected=true]:text-black"
                  }}
                >
                  <Tab
                    key="balance"
                    title={
                      <TabTitle
                        label={t('admin.partners.details.tabs.balance')}
                        badge={`$${parseFloat(balance).toFixed(2)}`}
                        isGreen={true}
                      />
                    }
                  />
                  <Tab
                    key="cpl-offers"
                    title={
                      <TabTitle
                        label={t('admin.partners.details.tabs.cplOffers')}
                        badge={cplOffersCount}
                      />
                    }
                  />
                  <Tab
                    key="revshare"
                    title={
                      <TabTitle
                        label={t('admin.partners.details.tabs.revshare')}
                        badge={revShareCampaignsCount}
                      />
                    }
                  />
                  <Tab
                    key="withdrawal-requests"
                    title={
                      <TabTitle
                        label={t('admin.partners.details.tabs.withdrawalRequests')}
                        badge={withdrawalRequestsCount}
                      />
                    }
                  />
                </Tabs>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerDetailsPage;
